import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Brain, Activity } from 'lucide-react';
import { governanceAPI } from '../../lib/api';

export default function AdminAIGovernance() {
  const [feed, setFeed] = useState([]);
  const [summary, setSummary] = useState({ by_model: [], total: 0, days: 30 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([governanceAPI.aiDecisionsFeed(100), governanceAPI.aiDecisionsSummary(30)])
      .then(([f, s]) => {
        setFeed(f.data?.decisions || []);
        setSummary(s.data || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-ai-governance-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Brain className="w-6 h-6 text-[#B89B5E]" /> AI Model Governance</h1>
        <p className="text-sm text-[#5B616E] mt-1">Audit trail of every AI decision: tagging, alt-text, moderation, summarisation. {summary.days || 30}-day window.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Total decisions</CardTitle></CardHeader>
          <CardContent><p className="text-3xl font-bold">{summary.total || 0}</p></CardContent>
        </Card>
        <Card className="bg-white border-[#E7E0D6] md:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm">By model & decision type</CardTitle></CardHeader>
          <CardContent>
            {(summary.by_model || []).length === 0 ? <p className="text-xs text-[#5B616E]">No data.</p> : (
              <div className="space-y-1">
                {summary.by_model.map((row, i) => (
                  <div key={i} className="flex items-center text-xs">
                    <span className="font-mono w-32 truncate">{row.model}</span>
                    <Badge variant="outline" className="text-[10px]">{row.type}</Badge>
                    <span className="ml-auto font-medium">{row.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity className="w-3.5 h-3.5" /> Recent decisions</CardTitle></CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-48" /> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-[#5B616E]">
                <tr>
                  <th className="text-left py-1">When</th>
                  <th className="text-left py-1">Model</th>
                  <th className="text-left py-1">Type</th>
                  <th className="text-left py-1">Input</th>
                  <th className="text-left py-1">Output</th>
                </tr>
              </thead>
              <tbody>
                {feed.map((d) => (
                  <tr key={d.id} className="border-t border-[#E7E0D6]">
                    <td className="py-1.5 pr-2 whitespace-nowrap">{new Date(d.created_at).toLocaleString()}</td>
                    <td className="py-1.5 pr-2 font-mono">{d.model}</td>
                    <td className="py-1.5 pr-2">{d.decision_type}</td>
                    <td className="py-1.5 pr-2 max-w-[180px] truncate">{d.input_summary}</td>
                    <td className="py-1.5 pr-2 max-w-[260px] truncate">{d.output_summary}</td>
                  </tr>
                ))}
                {feed.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#5B616E]">No decisions logged yet.</td></tr>}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
